<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    @on-confirm="saveStates"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="80%"
  >
    <v-row justify="center">
      <v-expansion-panels popout>
        <v-expansion-panel
          v-for="(state, statesIndex) in states"
          :key="statesIndex"
        >
          <v-expansion-panel-header>
            <template v-slot:default="{ open }">
              <v-row no-gutters>
                <v-col>
                  <v-fade-transition leave-absolute>
                    <span v-if="open" key="0">
                      {{ `${$t("DepartureState")}: ${state.description}` }}
                    </span>
                    <span v-else key="1">
                      {{ state.description }}
                    </span>
                  </v-fade-transition>
                </v-col>
              </v-row>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col align-self="center">
                <v-switch
                  v-model="state.notifyOnStateChange"
                  :label="$t('AlertOnStateChange')"
                ></v-switch>
              </v-col>
              <v-col align-self="center">
                <v-switch
                  v-model="state.notifyOnExpiration"
                  :label="$t('AlertOnExpiration')"
                ></v-switch>
              </v-col>
              <v-col
                cols="2"
                align-self="center"
                v-if="state.notifyOnExpiration"
              >
                <v-text-field
                  v-model="state.numberOfDaysNotif"
                  :label="$t('Days')"
                  min="1"
                  step="1"
                  type="number"
                ></v-text-field>
              </v-col>
              <v-col v-if="!flow.activeNotif" class="warning--text font-italic" align-self="center">
                {{ $t("FlowNotificationsDisabledWarning") }}
              </v-col>
            </v-row>
            <v-row
              v-if="state.transitions.length == 0"
              justify="center"
              align="center"
            >
              <v-col cols="1">
                <v-btn
                  tile
                  color="primary"
                  prepend-icon="mdi-plus"
                  outlined
                  @click="addStateTransitions(state)"
                >
                  {{ $t("Add") }}
                </v-btn>
              </v-col>
            </v-row>

            <div
              v-for="(item, transitionsIndex) in state.transitions"
              :key="transitionsIndex"
            >
              <v-row align="center">
                <v-col cols="1">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        tile
                        large
                        color="error"
                        icon
                        @click="removeStateTransitions(state, item.uuid)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("Delete") }}</span>
                  </v-tooltip>
                </v-col>
                <v-col>
                  <v-select
                    :label="$t('TransitionType')"
                    v-model="item.transitionType"
                    :items="transitionTypes"
                    item-text="text"
                    item-value="id"
                  ></v-select>
                </v-col>
                <v-col>
                  <v-text-field
                    readonly
                    v-model="item.displayConditionStr"
                    :label="$t('Condition')"
                    :append-icon="
                      item.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'
                    "
                    @click:append="editCondition(item)"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-select
                    :label="$t('DestinationState')"
                    v-model="item.stateTo"
                    :items="states"
                    item-text="description"
                    item-value="id"
                  ></v-select>
                </v-col>
                <v-col>
                  <v-select
                    multiple
                    :label="$t('Roles')"
                    v-model="item.roleTypeIDs"
                    :items="rolesList"
                    item-text="text"
                    item-value="id"
                    :error-messages="roleErrors(statesIndex, transitionsIndex)"
                  ></v-select>
                </v-col>
                <v-col>
                  <v-select
                    :label="$t('DigitalSignature')"
                    clearable
                    v-model="item.digitalSignatureConfigId"
                    :items="digitalSignaturesList"
                    item-text="text"
                    item-value="id"
                  ></v-select>
                </v-col>
                <v-col>
                  <v-btn
                    color="blue darken-4"
                    outlined
                    @click="editActions(item)"
                  >
                    {{ $t("Actions") }}
                    <v-icon v-if="item.actions.length > 0" right>
                      mdi-pencil
                    </v-icon>
                    <v-icon v-if="item.actions.length == 0" right>
                      mdi-plus
                    </v-icon>
                  </v-btn>
                </v-col>
                <v-col cols="1">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        tile
                        large
                        color="primary"
                        icon
                        @click="addStateTransitions(state)"
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("Add") }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-row>
    <SearchCriteriaBuilderModal
      :ref="conditionModalKey"
      :contentTypeIDs="flow.contentTypeIDs"
      @on-condition-saved="onConditionChanged"
      :allowEmptyCondition="true"
    />
    <ActionsModal
      :ref="actionsModalKey"
      :contentTypeIDs="flow.contentTypeIDs"
      @on-saved="onActionsSaved"
    />
    <FlowDiagramModal :ref="flowDiagramModalKey" />
    <template v-slot:actions>
      <div style="position: absolute">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              text
              small
              color="primary"
              @click="showFlowDiagram"
            >
              <v-icon large>mdi-state-machine</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("FlowChart") }}</span>
        </v-tooltip>
      </div>
    </template>
  </base-modal>
</template>
<script>
import SearchCriteriaBuilderModal from "@/components/search/SearchCriteriaBuilderModal.vue";
import ActionsModal from "@/components/administration/flow/ActionsModal.vue";
import FlowDiagramModal from "@/components/administration/flow/FlowDiagramModal.vue";
import Flow from "@/ecm/models/workflow/Flow";
import State from "@/ecm/models/workflow/State";
import StateTransition from "@/ecm/models/workflow/StateTransition";
import FlowService from "@/services/FlowService";
import RoleService from "@/services/RoleService";
import DigitalSignatureService from "@/services/DigitalSignatureService";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";

export default {
  name: "StatesModal",
  components: {
    SearchCriteriaBuilderModal,
    ActionsModal,
    FlowDiagramModal,
  },
  mounted() {
    this.getRoles();
    this.getDigitalSignatures();
    this.$v.$reset();
  },
  data() {
    return {
      modal: {
        title: this.$t("States"),
        key: "statesModal",
      },
      flow: new Flow(),
      open: false,
      conditionModalOpen: false,
      states: [],
      currentTransition: new StateTransition(),
      rolesList: [],
      transitionTypes: [
        { id: "V", text: this.$t("Validate") },
        { id: "R", text: this.$t("Refuse") },
      ],
      digitalSignaturesList: [],
      conditionModalKey: "conditionModalKey",
      actionsModalKey: "actionModalKey",
      flowDiagramModalKey: "flowDiagramModalKey",
    };
  },
  mixins: [validationMixin],
  validations: {
    states: {
      $each: {
        transitions: {
          $each: {
            roleTypeIDs: { required },
            stateTo: {},
          },
        },
      },
    },
  },
  computed: {
    modalTitle() {
      return `${this.modal.title} - ${this.flow.name}`;
    },
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    conditionModalInstance() {
      return this.$refs[this.conditionModalKey];
    },
    actionsModalInstance() {
      return this.$refs[this.actionsModalKey];
    },
    flowDiagramModalInstance() {
      return this.$refs[this.flowDiagramModalKey];
    },
  },
  methods: {
    roleErrors(statesIndex, transitionsIndex) {
      const errors = [];
      if (
        !this.$v.states.$each[statesIndex].transitions.$each[transitionsIndex]
          .roleTypeIDs.$dirty
      )
        return errors;
      !this.$v.states.$each[statesIndex].transitions.$each[transitionsIndex]
        .roleTypeIDs.required && errors.push(this.$t("RequiredField"));
      return errors;
    },
    // stateToErrors(statesIndex, transitionsIndex) {
    //   const errors = [];
    //   if (!!this.$v.states.$each[statesIndex].transitions.$each[transitionsIndex].stateTo.$dirty)
    //     return errors;
    //   !!this.$v.states.$each[statesIndex].transitions.$each[transitionsIndex].stateTo.required &&
    //     errors.push(this.$t("RequiredField"));
    //   return errors;
    // },
    edit(dto) {
      this.flow = new Flow(dto);
      this.getStates();
      this.show();
    },
    editCondition(item) {
      this.currentTransition = item;
      this.conditionModalInstance.edit(item.conditionStr);
    },
    onConditionChanged(condition) {
      this.currentTransition.conditionStr = condition.conditionStr;
      this.currentTransition.displayConditionStr =
        condition.displayConditionStr;
    },
    editActions(item) {
      this.currentItem = item;
      this.actionsModalInstance.edit(item.actions);
    },
    showFlowDiagram() {
      this.flowDiagramModalInstance.fromFlow(this.flow.name, this.states);
    },
    onActionsSaved(actions) {
      this.currentItem.actions = actions;
    },
    getStates() {
      FlowService.getStates(this.flow.id).then((data) => {
        this.states = data.map((dto) => new State(dto));
      });
    },
    getRoles() {
      RoleService.getList().then((data) => {
        this.rolesList = data;
      });
    },
    getDigitalSignatures() {
      DigitalSignatureService.getList().then((data) => {
        this.digitalSignaturesList = data;
      });
    },
    saveStates() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.modalInstance.progress(true);

      var states = this.states.map((item) => item.toDto());
      FlowService.saveStates(states)
        .then((data) => {
          this.hide();
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    addStateTransitions(state) {
      var newTransition = new StateTransition();
      newTransition.roleTypeIDs = [this.rolesList[0].id];
      var stateTo = this.states.find((x) => x.id != state.id);
      if (!stateTo) {
        stateTo = state;
      }
      newTransition.stateTo = stateTo.id;
      state.transitions.push(newTransition);
    },
    removeStateTransitions(state, uuid) {
      state.transitions = state.transitions.filter(
        (item) => item.uuid !== uuid
      );
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
  },
};
</script>
